import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, DollarSign, AlertTriangle, Activity, Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useBrand } from "@/config/brands";
import { useTheme } from "@/contexts/ThemeContext";
import { useTranslation } from 'react-i18next';

interface MarkupData {
  avg_gross_markup_percentage?: number;
  cpt_avg_markup_percentage?: number;
  drg_avg_markup_percentage?: number;
  rev_avg_markup_percentage?: number;
  ndc_avg_markup_percentage?: number;
  cpt_procedure_count?: number;
  drg_procedure_count?: number;
  rev_procedure_count?: number;
  ndc_procedure_count?: number;
  total_procedures?: number;
}

interface TotalCounts {
  cpt?: number;
  drg?: number;
  revenue?: number;
  ndc?: number;
  other?: number;
  cpt_total?: number;
  drg_total?: number;
  revenue_total?: number;
  ndc_total?: number;
}

interface HospitalMarkupOverviewProps {
  markupData?: MarkupData;
  hospitalName: string;
  totalCounts?: TotalCounts;
}

export const HospitalMarkupOverview = ({ markupData, hospitalName, totalCounts }: HospitalMarkupOverviewProps) => {
  const brand = useBrand();
  const isAhp = brand.key === 'ahp';
  const { isDark } = useTheme();
  const { t } = useTranslation(['hospital', 'common']);

  // Show the component if we have markup data OR if we have total counts to display
  const hasMarkupData = markupData?.avg_gross_markup_percentage !== null && markupData?.avg_gross_markup_percentage !== undefined;
  const hasTotalCounts = totalCounts && Object.values(totalCounts).some(count => (count || 0) > 0);

  if (!hasMarkupData && !hasTotalCounts) {
    return null;
  }

  const getMarkupColor = (markup: number) => {
    if (markup < 0) return "text-green-500"; // Negative markup = cheaper = good
    if (markup >= 1000) return "text-red-500";
    if (markup >= 500) return "text-orange-500";
    if (markup >= 300) return "text-yellow-500";
    if (markup >= 100) return "text-yellow-400";
    return "text-blue-400"; // Low positive markup
  };

  const getMarkupBadgeColor = (markup: number) => {
    if (markup < 0) return "bg-green-500/20 text-green-400 border-green-500/30";
    if (markup >= 1000) return "bg-red-500/20 text-red-400 border-red-500/30";
    if (markup >= 500) return "bg-orange-500/20 text-orange-400 border-orange-500/30";
    if (markup >= 300) return "bg-yellow-500/20 text-yellow-400 border-yellow-500/30";
    if (markup >= 100) return "bg-yellow-400/20 text-yellow-300 border-yellow-400/30";
    return "bg-blue-500/20 text-blue-400 border-blue-500/30";
  };

  // Hospital-level headline metric: median-baseline markup (what the national
  // rank is ordered by); mean fallback until caches carry the field.
  const overallMarkup = (markupData as any)?.median_gross_markup_percentage ?? markupData?.avg_gross_markup_percentage;

  const codeTypeMarkups = [
    {
      label: t('hospital:markupOverview.codeTypes.cpt.label'),
      fullLabel: t('hospital:markupOverview.codeTypes.cpt.fullLabel'),
      markup: markupData?.cpt_avg_markup_percentage,
      analysisCount: markupData?.cpt_procedure_count,
      totalCount: totalCounts?.cpt_total,
      borderColor: '#60a5fa',
      textColor: '#60a5fa',
      icon: Activity,
      description: t('hospital:markupOverview.codeTypes.cpt.description')
    },
    {
      label: t('hospital:markupOverview.codeTypes.drg.label'),
      fullLabel: t('hospital:markupOverview.codeTypes.drg.fullLabel'),
      markup: markupData?.drg_avg_markup_percentage,
      analysisCount: markupData?.drg_procedure_count,
      totalCount: totalCounts?.drg_total,
      borderColor: '#c084fc',
      textColor: '#c084fc',
      icon: AlertTriangle,
      description: t('hospital:markupOverview.codeTypes.drg.description')
    },
    {
      label: t('hospital:markupOverview.codeTypes.revenue.label'),
      fullLabel: t('hospital:markupOverview.codeTypes.revenue.fullLabel'),
      markup: markupData?.rev_avg_markup_percentage,
      analysisCount: markupData?.rev_procedure_count,
      totalCount: totalCounts?.revenue_total,
      borderColor: '#4ade80',
      textColor: '#4ade80',
      icon: TrendingUp,
      description: t('hospital:markupOverview.codeTypes.revenue.description')
    },
    {
      label: t('hospital:markupOverview.codeTypes.ndc.label'),
      fullLabel: t('hospital:markupOverview.codeTypes.ndc.fullLabel'),
      markup: markupData?.ndc_avg_markup_percentage,
      analysisCount: markupData?.ndc_procedure_count,
      totalCount: totalCounts?.ndc_total,
      borderColor: '#fb7185',
      textColor: '#fb7185',
      icon: DollarSign,
      description: t('hospital:markupOverview.codeTypes.ndc.description')
    },
    {
      label: t('hospital:markupOverview.codeTypes.cdm.label'),
      fullLabel: t('hospital:markupOverview.codeTypes.cdm.fullLabel'),
      markup: null,
      analysisCount: 0,
      totalCount: totalCounts?.other,
      borderColor: '#9ca3af',
      textColor: '#9ca3af',
      icon: Info,
      description: t('hospital:markupOverview.codeTypes.cdm.description')
    }
  ].filter(item => (item.totalCount || 0) > 0 || (item.analysisCount || 0) > 0); // Show all code types that have data

  return (
    <TooltipProvider>
      <div className="mb-8 space-y-6">
        {/* Overall Markup - only show if we have markup data */}
        {hasMarkupData && overallMarkup !== null && overallMarkup !== undefined && (
          <Card
            className={`p-6 ${isAhp ? '' : 'bg-gradient-to-r from-secondary/30 to-secondary/10 border-primary/20'}`}
            style={isAhp ? {
              backgroundColor: isDark ? 'rgba(10,22,40,0.85)' : 'rgba(255,255,255,0.9)',
              borderColor: isDark ? 'rgba(197,165,90,0.2)' : 'rgba(43,94,73,0.2)',
              borderWidth: '1px',
              borderStyle: 'solid',
            } : undefined}
          >
            <div className="flex items-center justify-between">
              <div>
                <h3
                  className={`text-2xl font-bold mb-2 ${isAhp ? (isDark ? 'text-white' : 'text-foreground') : 'text-terminal-green'}`}
                  style={isAhp ? { fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400 } : undefined}
                >
                  {t('hospital:sections.priceVsNationalAverage')}
                </h3>
                <p className="text-muted-foreground">
                  {t('hospital:sections.priceComparisonDescription', { hospitalName })}
                </p>
              </div>
              <div className="text-right">
                <div className={`text-5xl font-mono font-bold ${getMarkupColor(overallMarkup)} mb-2`}>
                  {overallMarkup >= 0 ? '+' : ''}{Math.round(overallMarkup)}%
                </div>
                <Badge className={`${getMarkupBadgeColor(overallMarkup)} font-mono`}>
                  {t('hospital:status.vsNationalAverage')}
                </Badge>
              </div>
            </div>
          </Card>
        )}


        {/* Total Procedures Count */}
        <div className="flex justify-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-secondary/50 rounded-full">
            <Activity className="w-4 h-4 text-primary" />
            <span className="text-sm text-muted-foreground">
              {t('hospital:metrics.totalProceduresAnalyzed')}
            </span>
            <span className="font-mono font-bold text-primary">
              {(markupData?.total_procedures || 0).toLocaleString()}
            </span>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};